'use client';

import { initClarity, setClarityConsentV2 } from '@/lib/clarity';
import { supabase } from '@/integrations/supabase/client';
import {
  COMMON_CURRENCY_CODES,
  getCountryName,
  getPreferredCurrencyForCountry,
  persistUserCountry,
  USER_COUNTRY_CODE_STORAGE_KEY,
} from '@/lib/location';
import { FBAdvancedMatching, initializeFBPixel, initializeGtm } from '@/lib/tracking';
import { initUet, setUetDefaultConsentDenied, updateUetConsent } from '@/lib/uet';
import posthog from 'posthog-js';
import * as React from 'react';
import { useEffect } from 'react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';

// Declare the fbq function type globally
declare global {
  interface Window {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
    fbq: Function;
    _fbq: any;
    dataLayer: any[];
  }
}

interface CookieSettings {
  necessary: boolean;
  analytics: boolean;
}

const scheduleWhenInteractive = (callback: () => void, timeoutMs = 15000) => {
  const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart', 'scroll'];

  const cleanup = () => {
    events.forEach((eventName) => window.removeEventListener(eventName, run));
    window.clearTimeout(timeoutId);
  };
  const run = () => {
    cleanup();
    callback();
  };

  events.forEach((eventName) =>
    window.addEventListener(eventName, run, { once: true, passive: true })
  );
  const timeoutId = window.setTimeout(run, timeoutMs);

  return cleanup;
};

async function initTrackers() {
  initUet();

  let advancedMatching: FBAdvancedMatching | undefined;

  try {
    const { data } = await supabase.auth.getUser();
    const user = data?.user;
    const metadata = user?.user_metadata || {};

    const firstName =
      metadata.first_name ||
      metadata.firstname ||
      metadata.given_name;
    const lastName =
      metadata.last_name ||
      metadata.lastname ||
      metadata.family_name;
    const city = metadata.city;
    const state = metadata.state || metadata.region;
    const zip = metadata.zip || metadata.postal_code;
    const country = metadata.country || metadata.country_code;
    const gender = metadata.gender;
    const birthDate =
      metadata.date_of_birth ||
      metadata.birth_date ||
      metadata.birthday ||
      metadata.dob;

    if (user) {
      advancedMatching = {
        em: user.email || undefined,
        ph: user.phone || undefined,
        fn: firstName || undefined,
        ln: lastName || undefined,
        ge: gender || undefined,
        db: birthDate || undefined,
        ct: city || undefined,
        st: state || undefined,
        zp: zip || undefined,
        country: country || undefined,
        external_id: user.id,
      };
    }
  } catch (error) {
    console.error('Unable to load Supabase user for FB advanced matching:', error);
  }

  initializeFBPixel('3013261452165294', advancedMatching);
  // if page is /success, track purchase success
  initializeGtm('G-RM7RJKZW0W'); // analytics
  // facebook tracked via server side conversion API
  initClarity();
  setClarityConsentV2(true);
}

async function getPublicIP() {
  const res = await fetch('https://api.ipify.org?format=json');
  if (!res.ok) throw new Error(res.statusText);
  const { ip } = await res.json();
  return ip;
}

const EU_COUNTRIES = [
  'AT',
  'BE',
  'BG',
  'HR',
  'CY',
  'CZ',
  'DK',
  'EE',
  'FI',
  'FR',
  'DE',
  'GR',
  'HU',
  'IE',
  'IT',
  'LV',
  'LT',
  'LU',
  'MT',
  'NL',
  'PL',
  'PT',
  'RO',
  'SK',
  'SI',
  'ES',
  'SE',
];

export function CookieBanner() {
  const [open, setOpen] = React.useState(false);
  const [settings, setSettings] = React.useState<CookieSettings>({
    necessary: true,
    analytics: false,
  });

  const [consent, setConsent] = React.useState<CookieSettings | null>(() => {
    const stored =
      localStorage.getItem('cookie-consent') ||
      sessionStorage.getItem('cookie-consent');
    if (!stored) return null;
    try {
      return JSON.parse(stored) as CookieSettings;
    } catch {
      return null;
    }
  });
  const hasInitialized = React.useRef(false);
  const wasOpenRef = React.useRef(false);
  const autoAcceptTrackedRef = React.useRef(false);
  const startTrackers = React.useCallback(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;
    void initTrackers();
  }, []);

  // Cache isEU result in sessionStorage to avoid repeated geo lookups in the same session
  const [isEU, setIsEU] = React.useState<null | boolean>(() => {
    const cached = sessionStorage.getItem('is-eu');
    return cached !== null ? JSON.parse(cached) : null;
  });

  React.useEffect(() => {
    if (isEU === null) {
      (async () => {
        try {
          const ip = await getPublicIP();
          const response = await fetch(
            'https://arzdpniijwnzlpkukabn.supabase.co/functions/v1/geoip',
            {
              method: 'GET',
              headers: {
                'X-Forwarded-For': ip,
              },
            }
          );
          const data = await response.json();
          const code = data.countryShort;
          const eu = EU_COUNTRIES.includes(code);
          setIsEU(eu);
          sessionStorage.setItem('is-eu', JSON.stringify(eu));

          if (code && !localStorage.getItem(USER_COUNTRY_CODE_STORAGE_KEY)) {
            persistUserCountry(code, data.countryLong || getCountryName(code));
          }

          // Set default currency based on country
          if (!localStorage.getItem('user-currency')) {
            const currency = getPreferredCurrencyForCountry(code || 'US', COMMON_CURRENCY_CODES);
            localStorage.setItem('user-currency', currency);
            // Dispatch event to notify that currency has been set
            window.dispatchEvent(new CustomEvent('currency-initialized'));
          } else {
            // Currency already exists, still dispatch event
            window.dispatchEvent(new CustomEvent('currency-initialized'));
          }
        } catch (err) {
          setIsEU(true); // assume eu true if something goes wrong
          sessionStorage.setItem('is-eu', JSON.stringify(true));
          if (!localStorage.getItem(USER_COUNTRY_CODE_STORAGE_KEY)) {
            persistUserCountry('US', 'United States');
          }
          if (!localStorage.getItem('user-currency')) {
            localStorage.setItem('user-currency', 'USD');
          }
          // Dispatch event even on error
          window.dispatchEvent(new CustomEvent('currency-initialized'));
        }
      })();
    }
  }, [isEU]);

  useEffect(() => {
    const sessionConsent = sessionStorage.getItem('cookie-consent');
    const localConsent = localStorage.getItem('cookie-consent');

    if (sessionConsent && !localConsent) {
      localStorage.setItem('cookie-consent', sessionConsent);
    }

    if (sessionConsent) {
      sessionStorage.removeItem('cookie-consent');
    }
  }, []);

  useEffect(() => {
    setUetDefaultConsentDenied();
  }, []);

  useEffect(() => {
    if (isEU == null) {
      return;
    }
    if (consent?.analytics && !hasInitialized?.current) {
      updateUetConsent(true);
      return scheduleWhenInteractive(startTrackers);
    }
    if (isEU === false) {
      if (!autoAcceptTrackedRef.current) {
        posthog.capture('marketing_consent_updated', {
          marketing_accepted: true,
          source: 'non_eu_default',
          has_existing_consent: !!consent,
          is_eu: isEU,
        });
        autoAcceptTrackedRef.current = true;
      }
      updateUetConsent(true);
      return scheduleWhenInteractive(startTrackers);
    } else if (isEU === true) {
      if (!consent) {
        updateUetConsent(false);
        setOpen(true);
        return;
      }
      updateUetConsent(!!consent.analytics);
      initClarity();
      setClarityConsentV2(!!consent.analytics);
      setOpen(false);
    }
  }, [isEU, consent, startTrackers]);

  useEffect(() => {
    const handleOpenCookieSettings = () => {
      setOpen(true);
    };

    window.addEventListener('open-cookie-settings', handleOpenCookieSettings);
    return () => {
      window.removeEventListener('open-cookie-settings', handleOpenCookieSettings);
    };
  }, []);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      posthog.capture('cookie_banner_shown', {
        analytics_enabled: settings.analytics,
        has_existing_consent: !!consent,
        is_eu: isEU,
      });
    }

    wasOpenRef.current = open;
  }, [consent, isEU, open, settings.analytics]);

  const handleAccept = (acceptAll: boolean) => {
    const newSettings = {
      necessary: true,
      analytics: acceptAll ? true : settings.analytics,
    };

    posthog.capture('cookie_banner_clicked', {
      action: acceptAll ? 'accept_all' : 'save_preferences',
      analytics_enabled: newSettings.analytics,
      has_existing_consent: !!consent,
      is_eu: isEU,
    });

    posthog.capture('marketing_consent_updated', {
      marketing_accepted: acceptAll,
      source: acceptAll ? 'accept_all' : 'save_preferences',
      has_existing_consent: !!consent,
      is_eu: isEU,
    });

    setSettings(newSettings);
    setConsent(newSettings);
    localStorage.setItem('cookie-consent', JSON.stringify(newSettings));
    sessionStorage.removeItem('cookie-consent');
    window.dispatchEvent(
      new CustomEvent('cookie-consent', { detail: newSettings })
    );
    if (newSettings.analytics) {
      updateUetConsent(true);
      startTrackers();
    } else {
      updateUetConsent(false);
      initClarity();
      setClarityConsentV2(false);
    }

    setOpen(false);
  };

  // return empty if route is /flipbook/*
  if (window.location.pathname.startsWith('/flipbook/')) {
    return <></>;
  }

  if (!open) return null;

  return (
    <aside
      aria-label="Cookie preferences"
      className="fixed inset-x-0 bottom-0 z-50 border-t bg-background/95 shadow-2xl backdrop-blur"
    >
      <div className="container flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:py-5">
        <div className="max-w-2xl">
          <h2 className="font-semibold">Cookie preferences</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            We use necessary cookies to run the site and optional analytics cookies to understand traffic. Read our{' '}
            <a className="underline underline-offset-2 hover:text-foreground" href="/privacy-policy" target="_blank" rel="noreferrer">Cookie and Privacy Policy</a>.
          </p>
          <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2">
            <label className="flex items-center gap-2 text-sm font-medium"><Checkbox checked disabled />Necessary cookies</label>
            <label htmlFor="analytics" className="flex cursor-pointer items-center gap-2 text-sm font-medium">
              <Checkbox id="analytics" checked={settings.analytics} onCheckedChange={(checked) => setSettings({ ...settings, analytics: checked as boolean })} />Analytics cookies
            </label>
          </div>
        </div>
        <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row">
          <Button type="button" variant="outline" onClick={() => handleAccept(false)}>Save Preferences</Button>
          <Button type="button" onClick={() => handleAccept(true)}>Accept All</Button>
        </div>
      </div>
    </aside>
  );
}
